import { useEffect } from "react";
import { listPresence, useUserStore } from "@slumbr/core";

import { usePresenceStore } from "@/stores/presence-store";
import {
  startEphemeralPresence,
  type EphemeralPresenceController,
} from "@/realtime/ephemeral-presence";

/**
 * Drives the user's "I'm online" signal without HTTP calls on the hot path.
 *
 * Every authenticated tab runs its own heartbeat. We deliberately do NOT
 * leader-gate this work for two reasons:
 *
 *   1. Each tab needs its own `selfStatus` in the presence store so the
 *      top-bar / nav avatar dot renders correctly. If only the leader
 *      published, follower tabs would show offline forever.
 *
 *   2. A hidden / backgrounded leader can be throttled or frozen by the
 *      browser. Its setInterval may not fire for many minutes, the cache
 *      TTL (15 min) expires, and every *other* user sees us as offline
 *      until we close + reopen. By heartbeating from every tab, the
 *      visible tab keeps the cache fresh regardless of what hidden
 *      siblings are doing.
 *
 * The cost is small: 1 cache write + 1 topic publish per ~60s per tab
 * (5 min when hidden). For a typical user with 1-2 tabs this is
 * negligible. Leader gating is preserved for *subscription* fanout where
 * the savings are large (see [[use-realtime-subscriptions]]).
 */
export function usePresenceHeartbeat() {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const userId = useUserStore((state) => state.user?.id);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let cancelled = false;
    let controller: EphemeralPresenceController | null = null;

    // Hydrate the presence store with the user's persisted visibility
    // BEFORE the first heartbeat publishes. Without this the store
    // defaults to "online" on every reload — a privacy hole for anyone
    // who previously set "Appear offline" — and the first heartbeat
    // would broadcast the wrong state to every subscriber.
    void (async () => {
      try {
        const res = await listPresence([userId]);
        if (cancelled) return;
        const own = res.items.find((item) => item.userId === userId);
        if (own) usePresenceStore.getState().setLocalSelfStatus(own);
      } catch {
        // Auth/network failure — fall back to the store's default
        // visibility. The first heartbeat publishes that.
      }
      if (cancelled) return;
      controller = startEphemeralPresence(userId);
    })();

    return () => {
      cancelled = true;
      controller?.stop();
      controller = null;
    };
  }, [isAuthenticated, userId]);
}
